import Toast from '@shared/ui/Toast/Toast';
import { ToastContext } from '@shared/ui/Toast/ToastContext';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { selectUserId } from '@store/reducers/authSlice';
import { normalizeProps, Portal, useMachine } from '@zag-js/react';
import * as toast from '@zag-js/toast';
import { type FC, memo, type PropsWithChildren, useEffect, useId, useRef } from 'react';

import SocketService from './api/notificationService';
import { removeToast, selectNotificationToasts, sendNotification } from './notificationSlice';

interface NotificationProviderProps extends PropsWithChildren {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  controls?: any;
}

const toaster = toast.createStore({
  overlap: true,
  placement: 'bottom-start',
  removeDelay: 200,
  offsets: '18px',
});

const NotificationProvider: FC<NotificationProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const toasts = useAppSelector(selectNotificationToasts);
  const toasterRef = useRef(toaster);

  const userId = useAppSelector(selectUserId);
  const ioConnected = useRef(false);

  const service = useMachine(toast.group.machine, {
    id: useId(),
    store: toasterRef.current,
  });

  const toastApi = toast.group.connect(service, normalizeProps);

  useEffect(() => {
    if (userId && !ioConnected.current) {
      const socketService = new SocketService();
      socketService.bindUser(String(userId));
      socketService.onNotification(notification => {
        dispatch(sendNotification(notification));
      });
      ioConnected.current = true;
    }
  }, [userId, dispatch]);

  useEffect(() => {
    for (const notification of toasts) {
      toasterRef.current.create({
        title: notification.title,
        description: notification.description,
        type: notification.type,
        duration: 1000 * 10,
      });

      // Cleanup Redux after toast expires
      dispatch(removeToast(notification.id));
    }
  }, [toasts, dispatch]);

  return (
    <ToastContext.Provider value={toasterRef.current}>
      <Portal>
        <div {...toastApi.getGroupProps()}>
          {toastApi.getToasts().map((toast, index) => (
            <Toast key={toast.id} actor={toast} parent={service} index={index} />
          ))}
        </div>
      </Portal>
      {children}
    </ToastContext.Provider>
  );
};

export default memo(NotificationProvider);
